import { sql } from "drizzle-orm";
import {
  boolean,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

export const emailProviderEnum = pgEnum("email_provider", ["microsoft_graph"]);
export const emailDirectionEnum = pgEnum("email_direction", ["inbound", "outbound"]);
export const emailMailboxSyncStatusEnum = pgEnum("email_mailbox_sync_status", [
  "active",
  "paused",
  "reauth_required",
  "error",
]);
export const emailBodyRedactionStateEnum = pgEnum("email_body_redaction_state", [
  "none",
  "redacted",
  "excluded",
]);
export const emailLinkEntityTypeEnum = pgEnum("email_link_entity_type", [
  "contact",
  "account",
  "deal",
  "client_group",
]);
export const emailLinkSourceEnum = pgEnum("email_link_source", [
  "manual",
  "auto",
  "token",
  "thread_inherited",
  "imported_submission",
]);
export const emailDedupKindEnum = pgEnum("email_dedup_kind", [
  "message",
  "change",
  "batch",
  "subscription_notification",
]);

const participantSchema = z
  .object({
    emailAddress: z
      .object({
        address: z.string().email().optional(),
        name: z.string().optional().nullable(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

const participantsSchema = z.array(participantSchema).optional().nullable();
const jsonRecordSchema = z.record(z.unknown()).optional().nullable();

export const emailMailboxes = pgTable(
  "crm_x_email_mailboxes",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id").notNull(),
    provider: emailProviderEnum("provider").notNull().default("microsoft_graph"),
    mailboxAddress: varchar("mailbox_address", { length: 320 }).notNull(),
    displayName: varchar("display_name", { length: 255 }),
    graphTenantId: varchar("graph_tenant_id", { length: 100 }),
    graphUserId: varchar("graph_user_id", { length: 255 }),
    folderScope: jsonb("folder_scope").$type<string[]>().notNull().default(sql`'["inbox","sentitems"]'::jsonb`),
    syncStatus: emailMailboxSyncStatusEnum("sync_status").notNull().default("paused"),
    lastSyncedAt: timestamp("last_synced_at", { withTimezone: true }),
    lastDeltaLink: text("last_delta_link"),
    lastError: text("last_error"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
    createdBy: uuid("created_by"),
    updatedBy: uuid("updated_by"),
  },
  (table) => ({
    orgProviderAddressUnique: uniqueIndex("uq_email_mailboxes_org_provider_address").on(
      table.organisationId,
      table.provider,
      table.mailboxAddress,
    ),
    scopeKeyUnique: uniqueIndex("uq_email_mailboxes_scope_key").on(
      table.organisationId,
      table.id,
      table.provider,
    ),
  }),
);

export const emailMailboxTokens = pgTable("crm_x_email_mailbox_tokens", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id").notNull(),
  mailboxId: uuid("mailbox_id")
    .notNull()
    .references(() => emailMailboxes.id, { onDelete: "cascade" }),
  provider: emailProviderEnum("provider").notNull().default("microsoft_graph"),
  encryptedRefreshToken: text("encrypted_refresh_token").notNull(),
  encryptionKeyId: varchar("encryption_key_id", { length: 120 }).notNull(),
  encryptionNonce: text("encryption_nonce").notNull(),
  encryptionTag: text("encryption_tag").notNull(),
  tokenScope: text("token_scope").notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const emailThreads = pgTable(
  "crm_x_email_threads",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id").notNull(),
    mailboxId: uuid("mailbox_id")
      .notNull()
      .references(() => emailMailboxes.id, { onDelete: "cascade" }),
    provider: emailProviderEnum("provider").notNull().default("microsoft_graph"),
    outlookConversationId: varchar("outlook_conversation_id", { length: 255 }).notNull(),
    subject: varchar("subject", { length: 500 }),
    preview: text("preview"),
    participantSummary: jsonb("participant_summary").$type<unknown[]>().notNull().default(sql`'[]'::jsonb`),
    messageCount: integer("message_count").notNull().default(0),
    firstMessageAt: timestamp("first_message_at", { withTimezone: true }),
    lastMessageAt: timestamp("last_message_at", { withTimezone: true }),
    threadLinkInheritanceEnabled: boolean("thread_link_inheritance_enabled").notNull().default(true),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    orgMailboxConversationUnique: uniqueIndex("uq_email_threads_org_mailbox_provider_conversation").on(
      table.organisationId,
      table.mailboxId,
      table.provider,
      table.outlookConversationId,
    ),
    orgIdUnique: uniqueIndex("uq_email_threads_org_id").on(table.organisationId, table.id),
  }),
);

export const emailMessages = pgTable(
  "crm_x_email_messages",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id").notNull(),
    mailboxId: uuid("mailbox_id")
      .notNull()
      .references(() => emailMailboxes.id, { onDelete: "cascade" }),
    threadId: uuid("thread_id").references(() => emailThreads.id, { onDelete: "set null" }),
    provider: emailProviderEnum("provider").notNull().default("microsoft_graph"),
    outlookMessageId: varchar("outlook_message_id", { length: 255 }).notNull(),
    outlookConversationId: varchar("outlook_conversation_id", { length: 255 }),
    internetMessageId: text("internet_message_id"),
    graphChangeKey: varchar("graph_change_key", { length: 255 }),
    outlookWebLink: text("outlook_web_link"),
    direction: emailDirectionEnum("direction").notNull(),
    subject: varchar("subject", { length: 500 }),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    receivedAt: timestamp("received_at", { withTimezone: true }),
    from: jsonb("from_participant").$type<Record<string, unknown>>(),
    sender: jsonb("sender").$type<Record<string, unknown>>(),
    toRecipients: jsonb("to_recipients").$type<unknown[]>().notNull().default(sql`'[]'::jsonb`),
    ccRecipients: jsonb("cc_recipients").$type<unknown[]>().notNull().default(sql`'[]'::jsonb`),
    bccRecipients: jsonb("bcc_recipients").$type<unknown[]>().notNull().default(sql`'[]'::jsonb`),
    replyTo: jsonb("reply_to").$type<unknown[]>().notNull().default(sql`'[]'::jsonb`),
    bodyText: text("body_text"),
    bodyHtml: text("body_html"),
    bodyPreview: text("body_preview"),
    bodyStorageKey: text("body_storage_key"),
    bodyContentHash: varchar("body_content_hash", { length: 128 }),
    // Provenance only: `redacted` records that ingest changed the body. Enforcement happens
    // before this row is written. Detector failures create a reference-only decision and no
    // email row, so they must never be represented by the silently hidden `excluded` value.
    bodyRedactionState: emailBodyRedactionStateEnum("body_redaction_state").notNull().default("none"),
    retentionExpiresAt: timestamp("retention_expires_at", { withTimezone: true }),
    hasAttachments: boolean("has_attachments").notNull().default(false),
    attachmentCount: integer("attachment_count").notNull().default(0),
    attachments: jsonb("attachments").$type<unknown[]>().notNull().default(sql`'[]'::jsonb`),
    categories: jsonb("categories").$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    importance: varchar("importance", { length: 20 }),
    isRead: boolean("is_read"),
    immutableProviderSnapshot: jsonb("immutable_provider_snapshot").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    orgMailboxMessageUnique: uniqueIndex("uq_email_messages_org_mailbox_provider_message").on(
      table.organisationId,
      table.mailboxId,
      table.provider,
      table.outlookMessageId,
    ),
    orgIdUnique: uniqueIndex("uq_email_messages_org_id").on(table.organisationId, table.id),
  }),
);

export const emailLinks = pgTable("crm_x_email_links", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id").notNull(),
  emailId: uuid("email_id")
    .notNull()
    .references(() => emailMessages.id, { onDelete: "cascade" }),
  threadId: uuid("thread_id").references(() => emailThreads.id, { onDelete: "set null" }),
  entityType: emailLinkEntityTypeEnum("entity_type").notNull(),
  entityId: uuid("entity_id").notNull(),
  linkSource: emailLinkSourceEnum("link_source").notNull(),
  confidence: integer("confidence"),
  included: boolean("included"),
  provenance: jsonb("provenance").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
  linkedBy: uuid("linked_by"),
  linkedAt: timestamp("linked_at", { withTimezone: true }).defaultNow().notNull(),
  unlinkedAt: timestamp("unlinked_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const emailIngestDedup = pgTable(
  "crm_x_email_ingest_dedup",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id").notNull(),
    mailboxId: uuid("mailbox_id")
      .notNull()
      .references(() => emailMailboxes.id, { onDelete: "cascade" }),
    provider: emailProviderEnum("provider").notNull().default("microsoft_graph"),
    dedupKind: emailDedupKindEnum("dedup_kind").notNull(),
    externalRef: text("external_ref").notNull(),
    payloadHash: varchar("payload_hash", { length: 128 }),
    firstSeenAt: timestamp("first_seen_at", { withTimezone: true }).defaultNow().notNull(),
    lastSeenAt: timestamp("last_seen_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => ({
    orgMailboxExternalRefUnique: uniqueIndex("uq_email_ingest_dedup_scope_external_ref").on(
      table.organisationId,
      table.mailboxId,
      table.provider,
      table.dedupKind,
      table.externalRef,
    ),
  }),
);

export const emailBodyChunks = pgTable("crm_x_email_body_chunks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id").notNull(),
  emailId: uuid("email_id")
    .notNull()
    .references(() => emailMessages.id, { onDelete: "cascade" }),
  chunkIndex: integer("chunk_index").notNull(),
  chunkText: text("chunk_text").notNull(),
  tokenCount: integer("token_count"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});

/**
 * Live projection between durable record-wide emails and application-scoped
 * submission communications (EDG-8484, plan item 10). A link references an email
 * message without copying its body; deleting the legacy submission communication
 * nulls `communicationId` (ON DELETE SET NULL) but never touches the email.
 */
export const emailSubmissionLinks = pgTable("crm_x_email_submission_links", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: uuid("organisation_id").notNull(),
  emailId: uuid("email_id")
    .notNull()
    .references(() => emailMessages.id, { onDelete: "cascade" }),
  applicationId: uuid("application_id").notNull(),
  submissionId: uuid("submission_id"),
  conditionId: uuid("condition_id"),
  communicationId: uuid("communication_id"),
  linkSource: emailLinkSourceEnum("link_source").notNull().default("auto"),
  matchMethod: varchar("match_method", { length: 50 }),
  included: boolean("included"),
  provenance: jsonb("provenance").$type<Record<string, unknown>>().notNull().default(sql`'{}'::jsonb`),
  linkedBy: uuid("linked_by"),
  linkedAt: timestamp("linked_at", { withTimezone: true }).defaultNow().notNull(),
  unlinkedAt: timestamp("unlinked_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const insertEmailMailboxSchema = createInsertSchema(emailMailboxes, {
  mailboxAddress: z.string().email(),
});
export const selectEmailMailboxSchema = createSelectSchema(emailMailboxes);

export const insertEmailMailboxTokenSchema = createInsertSchema(emailMailboxTokens);
export const selectEmailMailboxTokenSchema = createSelectSchema(emailMailboxTokens);

export const insertEmailThreadSchema = createInsertSchema(emailThreads);
export const selectEmailThreadSchema = createSelectSchema(emailThreads);

export const insertEmailMessageSchema = createInsertSchema(emailMessages, {
  from: participantSchema.optional().nullable(),
  sender: participantSchema.optional().nullable(),
  toRecipients: participantsSchema,
  ccRecipients: participantsSchema,
  bccRecipients: participantsSchema,
  replyTo: participantsSchema,
  immutableProviderSnapshot: jsonRecordSchema,
});
export const selectEmailMessageSchema = createSelectSchema(emailMessages);

export const insertEmailLinkSchema = createInsertSchema(emailLinks, {
  confidence: z.number().int().min(0).max(100).optional().nullable(),
  provenance: jsonRecordSchema,
});
export const selectEmailLinkSchema = createSelectSchema(emailLinks);

export const insertEmailIngestDedupSchema = createInsertSchema(emailIngestDedup);
export const selectEmailIngestDedupSchema = createSelectSchema(emailIngestDedup);

export const insertEmailBodyChunkSchema = createInsertSchema(emailBodyChunks);
export const selectEmailBodyChunkSchema = createSelectSchema(emailBodyChunks);

export const insertEmailSubmissionLinkSchema = createInsertSchema(emailSubmissionLinks, {
  provenance: jsonRecordSchema,
});
export const selectEmailSubmissionLinkSchema = createSelectSchema(emailSubmissionLinks);

export type EmailMailbox = typeof emailMailboxes.$inferSelect;
export type InsertEmailMailbox = typeof emailMailboxes.$inferInsert;
export type EmailMessage = typeof emailMessages.$inferSelect;
export type InsertEmailMessage = typeof emailMessages.$inferInsert;
export type EmailThread = typeof emailThreads.$inferSelect;
export type EmailLink = typeof emailLinks.$inferSelect;
export type EmailSubmissionLink = typeof emailSubmissionLinks.$inferSelect;
export type InsertEmailSubmissionLink = typeof emailSubmissionLinks.$inferInsert;
